import React from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { BookMarked, BookOpen, Headset, Home, LogOut, MessageSquare, Wallet } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useContact } from '../../hooks/useContact';
import useScrollToTop from '../../hooks/useScrollToTop';

const navItemClass = ({ isActive }: { isActive: boolean }) =>
  `inline-flex min-h-11 shrink-0 items-center gap-1.5 rounded-xl px-3 py-2 text-xs font-bold transition-all sm:gap-2 sm:px-3.5 sm:text-sm ${
    isActive
      ? 'bg-brand-600 text-white shadow-md shadow-brand-700/20 ring-2 ring-brand-500/50'
      : 'border-2 border-brand-100 bg-white text-slate-900 hover:border-brand-300 hover:bg-brand-50'
  }`;

const menuItems = [
  {
    path: '/',
    label: 'الصفحة الرئيسية',
    shortLabel: 'الرئيسية',
    icon: Home,
    end: true,
  },
  {
    path: '/student-portal',
    label: 'كورساتي وحصصي',
    shortLabel: 'كورساتي وحصصي',
    icon: BookMarked,
    end: true,
  },
  {
    path: '/student-portal/courses',
    label: 'الكورسات',
    shortLabel: 'الكورسات',
    icon: BookOpen,
    end: false,
  },
  {
    path: '/student-portal/wallet',
    label: 'محفظتي',
    shortLabel: 'محفظتي',
    icon: Wallet,
    end: false,
  },
  {
    path: '/student-portal/forum',
    label: 'المنتدى',
    shortLabel: 'المنتدى',
    icon: MessageSquare,
    end: false,
  },
];

const StudentPortalLayout: React.FC = () => {
  const { currentUser, logout } = useAuth();
  const navigate = useNavigate();
  const { openWhatsApp, whatsappNumber, isLoading: contactLoading } = useContact();
  useScrollToTop();

  const handleLogout = async () => {
    await logout();
    navigate('/login', { replace: true });
  };

  const handleSupportWhatsApp = () => {
    if (!whatsappNumber || contactLoading) return;
    openWhatsApp(
      `مرحباً، محتاج مساعدة في بوابة الطالب${currentUser?.name ? ` — ${currentUser.name}` : ''}`
    );
  };

  return (
    <div
      className="min-h-screen bg-[linear-gradient(180deg,#f0fdfa_0%,#f8fafc_40%,#ffffff_100%)]"
      dir="rtl"
    >
      <header className="sticky top-0 z-50 border-b-2 border-brand-100/80 bg-white/95 backdrop-blur-md">
        <div className="mx-auto flex h-14 max-w-6xl items-center justify-between gap-2 px-3 sm:h-16 sm:px-6">
          <NavLink to="/student-portal" className="flex min-w-0 items-center gap-2">
            <span
              className="inline-block h-7 w-1 shrink-0 rounded-full bg-brand-500"
              aria-hidden
            />
            <img
              src={`${process.env.PUBLIC_URL}/favicon.ico`}
              alt="المنصة"
              className="h-9 w-auto object-contain sm:h-10"
              width={120}
              height={40}
              decoding="async"
            />
          </NavLink>

          <div className="flex min-w-0 items-center gap-2 sm:gap-3">
            {currentUser?.name && (
              <span className="hidden max-w-[10rem] truncate rounded-full border border-brand-100 bg-white/80 px-3 py-1.5 text-sm font-semibold text-slate-900 sm:inline md:max-w-xs">
                {currentUser.name}
              </span>
            )}
            <button
              type="button"
              onClick={handleLogout}
              className="inline-flex min-h-10 items-center gap-1.5 rounded-xl border-2 border-transparent px-2.5 py-2 text-xs font-semibold text-red-600 transition-colors hover:border-red-200 hover:bg-red-50 sm:min-h-11 sm:px-3 sm:text-sm"
              aria-label="تسجيل الخروج"
            >
              <LogOut className="h-4 w-4 shrink-0" />
              <span className="hidden min-[400px]:inline">خروج</span>
            </button>
          </div>
        </div>

        <nav
          className="border-t border-brand-100/70 bg-gradient-to-b from-white to-brand-50/50"
          aria-label="قائمة بوابة الطالب"
        >
          <div className="mx-auto max-w-6xl px-3 py-2.5 sm:px-6 sm:py-3">
            <div className="-mx-1 flex gap-2 overflow-x-auto pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden sm:mx-0 sm:flex-wrap sm:justify-start sm:overflow-visible sm:pb-0">
              {menuItems.map((item) => (
                <NavLink
                  key={item.path}
                  to={item.path}
                  end={item.end}
                  className={navItemClass}
                >
                  <item.icon className="h-4 w-4 shrink-0 sm:h-[1.125rem] sm:w-[1.125rem]" />
                  <span className="whitespace-nowrap sm:hidden">{item.shortLabel}</span>
                  <span className="hidden whitespace-nowrap sm:inline">{item.label}</span>
                </NavLink>
              ))}
            </div>
          </div>
        </nav>
      </header>

      <main className="mx-auto min-h-[calc(100vh-8rem)] max-w-6xl overflow-x-hidden px-0">
        <Outlet />
      </main>

      <div className="fixed bottom-6 left-6 z-40 flex flex-col items-start gap-2">
        <span className="hidden rounded-full border border-brand-100 bg-white/95 px-3 py-1 text-xs font-semibold text-slate-900 shadow-sm sm:inline">
          دعم فني
        </span>
        <button
          type="button"
          onClick={handleSupportWhatsApp}
          disabled={!whatsappNumber || contactLoading}
          className="group relative flex h-14 w-14 items-center justify-center rounded-full bg-[#25D366] text-white shadow-lg shadow-green-700/30 transition hover:scale-105 hover:bg-[#1ebe57] disabled:cursor-not-allowed disabled:bg-slate-300 disabled:shadow-none"
          aria-label="تواصل مع الدعم الفني عبر واتساب"
          title={
            contactLoading
              ? 'جاري التحميل...'
              : whatsappNumber
                ? 'تواصل مع الدعم الفني'
                : 'رقم واتساب غير متوفر'
          }
        >
          {contactLoading ? (
            <span className="h-6 w-6 animate-spin rounded-full border-2 border-white border-t-transparent" />
          ) : (
            <Headset className="h-7 w-7" strokeWidth={2.25} />
          )}
          <span className="absolute -right-0.5 -top-0.5 flex h-5 w-5 items-center justify-center rounded-full bg-white shadow-sm">
            <svg className="h-3 w-3 text-[#25D366]" fill="currentColor" viewBox="0 0 24 24" aria-hidden>
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
            </svg>
          </span>
        </button>
      </div>
    </div>
  );
};

export default StudentPortalLayout;
